<template>
  <div
    class="tm-form-line"
    :class="{
      'tm-form-line--left-label': leftLabelComputed,
      'tm-form-line--overflow-initial': overflowInitial,
    }"
  >
    <div
      v-if="!hideLabel"
      :class="[
        'tm-form-line__label',
        {
          'tm-form-line__label--left': leftLabelComputed,
          [`tm-form-line__label--vertical-${labelVerticalAlign}`]: true,
        },
        labelClass,
      ]"
      :style="{
        width: labelWidth,
        'min-width': labelWidth,
        ...additionalStyles,
      }"
    >
      <slot
        name="label"
        v-bind="{ focus, label, required }"
      >
        <tm-form-line-label
          :label="label"
          :font-size="fontSize"
          :additional="labelAdditional"
          :extra-height-md="leftLabelComputed && !smallLabel"
          :required="required || requiredComputed"
          :distinct="labelDistinct"
          :ellipsis="ellipsis"
          @click="focus"
        >
          <slot name="label-text" />
        </tm-form-line-label>

        <slot name="after-label" />
      </slot>
    </div>
    <div
      v-if="!text && !hasTextSlot"
      ref="inputWrapRef"
      :class="inputWrapperClassList"
    >
      <slot />
    </div>
    <div
      v-else
      class="lh-40 font-weight-semi-bold"
    >
      <slot name="text">{{ text }}</slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, useSlots } from '@/composition/vue/compositionApi'
import { useFormInputRef, useFormLeftLabel, useFormLeftLabelWidth } from '@/composition/forms'
import type { TmFormLineVerticalAlign } from '@/components/shared/types'
import type { FormInputRef } from '@/components/forms/types'
import type { Dict } from '@/types'
import TmFormLineLabel, { type TmFormLineLabelFontSize } from '@/components/shared/TmFormLineLabel.vue'

type TmFormLineProps = {
  label?: string
  fontSize?: TmFormLineLabelFontSize
  labelDistinct?: boolean
  leftLabel?: boolean
  leftLabelWidth?: string
  smallLabel?: boolean // indicates that content is not a field and no need to add extra padding to TmFormLineLabel
  labelAdditional?: string
  text?: string
  required?: boolean
  inputWrapperClass?: string
  hideLabel?: boolean
  labelVerticalAlign?: TmFormLineVerticalAlign
  overflowInitial?: boolean
  labelClass?: string
  additionalStyles?: Dict
  disableFocus?: boolean
  ellipsis?: boolean
}

const props = withDefaults(defineProps<TmFormLineProps>(), {
  labelVerticalAlign: 'top',
  additionalStyles: () => ({}),
})

const slots = useSlots()

const leftLabelInjected = useFormLeftLabel().inject()
const leftLabelWidthInjected = useFormLeftLabelWidth().inject(ref('100px'))

const inputRef = ref<FormInputRef | null>(null)
useFormInputRef().provide(inputRef)

const inputWrapRef = ref<HTMLElement | null>(null)
const hasTextSlot = !!slots.text
const focus = () => {
  if (props.disableFocus) return
  inputRef.value?.focus?.()
}

const leftLabelComputed = computed(() => props.leftLabel || leftLabelInjected?.value)

const labelWidth = computed(() =>
  leftLabelComputed.value ? props.leftLabelWidth || leftLabelWidthInjected?.value : '100%',
)

const requiredComputed = computed(() => {
  if (inputWrapRef.value) {
    return inputWrapRef.value.querySelector('[validators*=required]') !== null
  }
  return false
})

const inputWrapperClassList = computed(() => {
  const classList: Record<string, boolean> = {
    'tm-form-line__input-wrapper': true,
    'tm-form-line__input-wrapper_left-label': !!leftLabelComputed.value,
  }

  if (props.inputWrapperClass) {
    classList[props.inputWrapperClass] = true
  }

  return classList
})

const getInputWrapElement = () => {
  return inputWrapRef.value
}

defineExpose({
  getInputWrapElement,
})
</script>

<style lang="scss" scoped>
.tm-form-line {
  .tm-form-line__label {
    display: flex;
    word-break: break-word;
    min-width: 0;

    &--vertical-top {
      align-items: flex-start;
    }
    &--vertical-center {
      align-items: center;
    }
  }

  // view with the left label
  &--left-label {
    @include media-md-min {
      display: flex;
    }
  }

  // special class to fix style overwriting when tm-form-line inside another tm-form-line with left-label prop
  &__label--left {
    @include media-md-min {
      margin-right: var(--horizontal-label-gap, 16px);
      justify-content: flex-end;
    }
  }

  &__input-wrapper {
    margin: -4px;
    padding: 4px;
    min-width: 0;

    &:not(:first-child) {
      padding-top: 8px;
    }

    &_left-label {
      @include media-md-min {
        flex-grow: 1;
        padding-top: 4px !important;
      }
    }
  }
  &--left-label:not(&--overflow-initial),
  &:not(&--overflow-initial) &__input-wrapper {
    overflow: hidden;
  }
}
</style>
